
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a theme-drawer.
 *
 * Dialog lifecycle is owned by theme-drawer.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.addEventListener(
      ThemeEvents.cartSectionRestored,
      this.#handleCartSectionRestored
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.removeEventListener(
      ThemeEvents.cartSectionRestored,
      this.#handleCartSectionRestored
    );
  }

  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this
        .querySelector('shopify-payment-terms')
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      window.matchMedia('(min-width: 750px)').matches &&
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => this.#updateStickyState());

        /*
         * TCG STOP
         * Sluit de winkelwagen automatisch wanneer
         * het laatste artikel is verwijderd.
         */
        if (
          event.action === 'remove' &&
          !detail?.didError &&
          Number(detail?.itemCount) === 0
        ) {
          window.setTimeout(() => {
            if (
              this.isConnected &&
              this.#themeDrawer?.isOpen &&
              this.#isCartEmpty()
            ) {
              this.#themeDrawer.close();
            }
          }, 300);
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );
      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
